import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { THEME } from '@anytag/shared-constants';
import { TextField } from '@anytag/shared-molecules';
import { YoutubeCmsChannelDetail } from '@anytag/graphql';
import { floatify, localizedDateFormatter } from '@anytag/shared-utils';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import { Button, TextCutter, MediaPreview, Anchor, Form } from '@anytag/shared-atoms';
import { useUpdateYoutubeChannelShareRate, YoutubeCMSChannelRevenueFormValues } from '@anytag/shared-organisms';
import { useYoutubeCMSContext } from '../YoutubeCMSContext';

interface YoutubeCMSChannelInformationProps {
  data: YoutubeCmsChannelDetail;
}

export const YoutubeCMSChannelInformation = ({ data }: YoutubeCMSChannelInformationProps) => {
  const { t } = useTranslation();
  const { canModifyYoutubeCMSData } = useYoutubeCMSContext();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const { handleSaveChange } = useUpdateYoutubeChannelShareRate();

  const profileLink = generateProfileLink(
    data.influencerId,
    data.socialAccountId ? { filter: { id: String(data.socialAccountId ?? '') } } : undefined
  );

  return (
    <div css={styles.channelInfo}>
      <div css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <MediaPreview src={data.avatar} css={{ width: '56px', height: '56px', borderRadius: '4px' }} />
        <div>
          <p css={{ fontSize: '16px', lineHeight: '100%', fontWeight: 600 }}>{data.channelName || '-'}</p>

          {profileLink.to ? (
            <Anchor
              variant="blue"
              label={t('Influencer account')}
              css={{ marginTop: '8px', fontSize: '12px', lineHeight: '100%' }}
              {...profileLink}
            />
          ) : null}

          <p css={{ marginTop: '8px', fontSize: '12px', lineHeight: '100%', color: THEME.text.colors.gray.lv3 }}>
            {t('id')}:&nbsp;{data.channelId || '-'}
          </p>
        </div>
      </div>

      <div>
        <Form.Provider<YoutubeCMSChannelRevenueFormValues>
          onSubmit={handleSaveChange}
          values={{
            [`${data.channelId}-shareRate`]:
              typeof data.shareRate === 'number' ? floatify(data.shareRate * 100).toString() : ''
          }}
        >
          {({ formState, onSubmit }) => (
            <div className="flex items-end gap-2">
              <TextField
                rightSection="%"
                type="number"
                placeholder="50"
                className="w-50 p-0"
                title={t('TextForm.Channel Marginrate')}
                disabled={!canModifyYoutubeCMSData || formState.isSubmitting}
                name={`${data.channelId}-shareRate` satisfies keyof YoutubeCMSChannelRevenueFormValues}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    onSubmit?.();
                  }
                }}
              />

              <Button
                type="submit"
                variant="solid"
                className="px-6"
                title={t('Button.Save')}
                disabled={!canModifyYoutubeCMSData || !formState.isDirty}
                loading={{ status: formState.isSubmitting, title: t('Button.Saving') }}
              />
            </div>
          )}
        </Form.Provider>

        <TextCutter
          className="mt-0.5 text-sm text-tertiary"
          text={`${t('Last Updated').toLowerCase()}: ${
            data.updatedDate ? localizedDateFormatter(data.updatedDate, 'yyyy/MM/dd') : '-'
          }`}
        />
      </div>
    </div>
  );
};
const styles = {
  channelInfo: css({
    gap: '16px',
    display: 'flex',
    padding: '24px 16px',
    alignItems: 'center',
    boxSizing: 'border-box',
    justifyContent: 'space-between',
    backgroundColor: THEME.background.colors.white,
    borderBottom: `1px solid ${THEME.border.colors.gray.lv1}`
  })
};
